import React, { useState, useMemo } from "react";
import { Row, Col } from "react-bootstrap";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from "recharts";

import AnalyticsHeader from "./AnalyticsHeader";
import AnalyticsFilters from "./AnalyticsFilters";
import StatisticsCards, { PerformanceSegments } from "./StatisticsCards";
import AttendanceChart from "./AttendanceChart";
import MarksDistributionChart, { PassFailDonut, TopBottomChart } from "./MarksChart";
import StudentLeaderboard, { TopPerformerCards, LowestPerformersTable } from "./StudentLeaderboard";
import ClassComparison from "./ClassComparison";
import SectionComparison from "./SectionComparison";
import SubjectComparison from "./SubjectComparison";
import { SubjectMarksBarChart, SubjectAccuracyRadar, SubjectRightWrongChart, RankDistributionChart } from "./SubjectPerformanceChart";
import { buildExamAnalytics, filterStudentRows, exportRowsToFile, printRows } from "./examAnalyticsHelpers";

function Section({ icon, title, children, right }) {
    return (
        <div className="lec-panel" style={{ border: "1px solid #f1f5f9", borderRadius: 12, padding: 16, background: "#fff", marginBottom: 16 }}>
            <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap" style={{ gap: 8 }}>
                <h6 className="font-weight-bold text-dark mb-0 d-flex align-items-center" style={{ gap: 8 }}>
                    <i className={icon} style={{ color: "#2563eb" }} />
                    {title}
                </h6>
                {right}
            </div>
            {children}
        </div>
    );
}

const GROUP_EXPORT_COLUMNS = [
    { dataField: "student_name", text: "Student" },
    { dataField: "class_name", text: "Class" },
    { dataField: "section_name", text: "Section" },
    { dataField: "marksNum", text: "Marks" },
    { dataField: "accuracyNum", text: "Accuracy" },
    { dataField: "exam_rank", text: "Exam Rank" },
    { dataField: "overall_rank", text: "Overall Rank" },
    { dataField: "passFail", text: "Status" },
];

function GroupedComparisonChart({ data, nameKey }) {
    if (!data || data.length === 0) {
        return <div className="text-center text-muted py-3" style={{ fontSize: 13 }}>No data available</div>;
    }
    return (
        <div style={{ width: "100%", height: 240 }}>
            <ResponsiveContainer>
                <BarChart data={data}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey={nameKey} tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip shared={false} />
                    <Legend />
                    <Bar dataKey="averageMarks" name="Avg Marks" fill="#2563eb" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="averageAccuracy" name="Avg Accuracy %" fill="#16a34a" radius={[4, 4, 0, 0]} />
                </BarChart>
            </ResponsiveContainer>
        </div>
    );
}

function ExamAnalyticsDashboard({
    exam,
    students,
    loading,
    error,
    classMap,
    sectionMap,
    classOptions,
    allSectionOptions,
    authorizedSubjectIds,
    onBack,
    onClose,
    parseExamStart,
    formatSectionsDisplay,
}) {
    const [search, setSearch] = useState("");
    const [classId, setClassId] = useState("");
    const [sectionIds, setSectionIds] = useState([]);
    const [subjectId, setSubjectId] = useState("");
    const [attendance, setAttendance] = useState("");
    const [passFail, setPassFail] = useState("");
    const [quickFilter, setQuickFilter] = useState("");

    // classOptions/allSectionOptions are already scoped to this lecturer's own access
    // by the parent dashboard (authHelper-based filtering) — reuse them as the
    // authorization boundary for the fetched student rows too, rather than trusting
    // the API's own section_id filter alone.
    const authorizedClassIds = useMemo(() => (classOptions || []).map((c) => String(c.value)), [classOptions]);
    const authorizedSectionIds = useMemo(() => (allSectionOptions || []).map((s) => String(s.value)), [allSectionOptions]);

    const analytics = useMemo(
        () => buildExamAnalytics(students, { classMap, sectionMap, authorizedSubjectIds, authorizedClassIds, authorizedSectionIds }),
        [students, classMap, sectionMap, authorizedSubjectIds, authorizedClassIds, authorizedSectionIds]
    );

    const sectionOptionsForFilter = useMemo(
        () => (classId ? (allSectionOptions || []).filter((s) => String(s.class_id) === String(classId)) : allSectionOptions || []),
        [allSectionOptions, classId]
    );

    const subjectOptions = useMemo(
        () => analytics.subjects.map((s) => ({ value: s.subjectId, label: s.subjectName })),
        [analytics.subjects]
    );

    const filteredRows = useMemo(() => {
        const list = filterStudentRows(analytics.rows, { search, classId, sectionIds, subjectId, attendance, passFail, quickFilter });
        return [...list].sort((a, b) => b.marksNum - a.marksNum);
    }, [analytics.rows, search, classId, sectionIds, subjectId, attendance, passFail, quickFilter]);

    const handleExportCsv = () => exportRowsToFile(filteredRows, GROUP_EXPORT_COLUMNS, `${exam.exam_name}_students`, "csv");
    const handleExportExcel = () => exportRowsToFile(filteredRows, GROUP_EXPORT_COLUMNS, `${exam.exam_name}_students`, "xlsx");
    const handlePrint = () => printRows(filteredRows, GROUP_EXPORT_COLUMNS, exam.exam_name);

    return (
        <div style={{ position: "fixed", inset: 0, background: "#f8fafc", zIndex: 1060, display: "flex", flexDirection: "column" }}>
            <AnalyticsHeader
                exam={exam}
                overall={analytics.overall}
                onBack={onBack}
                onClose={onClose}
                parseExamStart={parseExamStart}
                formatSectionsDisplay={formatSectionsDisplay}
            />

            {!loading && !error && analytics.rows.length > 0 && (
                <AnalyticsFilters
                    search={search}
                    onSearchChange={setSearch}
                    classOptions={classOptions}
                    classId={classId}
                    onClassChange={(v) => {
                        setClassId(v);
                        setSectionIds([]);
                    }}
                    sectionOptions={sectionOptionsForFilter}
                    sectionIds={sectionIds}
                    onSectionChange={setSectionIds}
                    subjectOptions={subjectOptions}
                    subjectId={subjectId}
                    onSubjectChange={setSubjectId}
                    attendance={attendance}
                    onAttendanceChange={setAttendance}
                    passFail={passFail}
                    onPassFailChange={setPassFail}
                    quickFilter={quickFilter}
                    onQuickFilterChange={setQuickFilter}
                    onExportCsv={handleExportCsv}
                    onExportExcel={handleExportExcel}
                    onPrint={handlePrint}
                />
            )}

            <div style={{ flex: 1, overflowY: "auto", padding: 20 }}>
                {loading ? (
                    <div>
                        {[...Array(4)].map((_, idx) => (
                            <div key={idx} className="lecture-exam-shim w-100 mb-3" style={{ height: 90 }} />
                        ))}
                    </div>
                ) : error ? (
                    <p className="text-danger text-center py-5">Unable to load student data for this exam.</p>
                ) : analytics.rows.length === 0 ? (
                    <div className="text-center py-5">
                        <i className="fal fa-users-slash text-muted" style={{ fontSize: 32 }} />
                        <p className="text-muted mt-2 mb-0">No Students Found</p>
                    </div>
                ) : (
                    <>
                        <Section icon="fal fa-chart-pie" title="Overview">
                            <StatisticsCards overall={analytics.overall} />
                            <Row className="mt-3 g-3">
                                <Col md={4}>
                                    <div className="text-muted mb-1" style={{ fontSize: 12.5, fontWeight: 600 }}>Attendance</div>
                                    <AttendanceChart overall={analytics.overall} />
                                </Col>
                                <Col md={4}>
                                    <div className="text-muted mb-1" style={{ fontSize: 12.5, fontWeight: 600 }}>Marks Distribution</div>
                                    <MarksDistributionChart data={analytics.marksDistribution} />
                                </Col>
                                <Col md={4}>
                                    <div className="text-muted mb-1" style={{ fontSize: 12.5, fontWeight: 600 }}>Pass / Fail</div>
                                    <PassFailDonut pass={analytics.overall.passCount} fail={analytics.overall.failCount} />
                                </Col>
                            </Row>
                        </Section>

                        <Section icon="fal fa-layer-group" title="Performance Segments">
                            <PerformanceSegments segments={analytics.segments} totalAttended={analytics.overall.attended} />
                        </Section>

                        <Section icon="fal fa-trophy" title="Top Performers Leaderboard">
                            <TopPerformerCards students={analytics.topPerformers} />
                            <Row className="mt-3 g-3">
                                <Col lg={7}>
                                    <StudentLeaderboard rows={filteredRows} />
                                </Col>
                                <Col lg={5}>
                                    <TopBottomChart data={analytics.topPerformers} color="#16a34a" height={280} />
                                </Col>
                            </Row>
                        </Section>

                        <Section icon="fal fa-hand-holding-heart" title="Students Needing Support">
                            <Row className="g-3">
                                <Col lg={7}>
                                    <LowestPerformersTable students={analytics.lowestPerformers} />
                                </Col>
                                <Col lg={5}>
                                    <TopBottomChart data={analytics.lowestPerformers} color="#dc2626" height={280} />
                                </Col>
                            </Row>
                        </Section>

                        <Section icon="fal fa-school" title="Class & Section Analytics">
                            <h6 className="text-muted mb-2" style={{ fontSize: 12.5, fontWeight: 700 }}>By Class</h6>
                            <GroupedComparisonChart data={analytics.classComparison} nameKey="name" />
                            <div className="mt-2 mb-4">
                                <ClassComparison data={analytics.classComparison} />
                            </div>
                            <h6 className="text-muted mb-2" style={{ fontSize: 12.5, fontWeight: 700 }}>By Section</h6>
                            <GroupedComparisonChart data={analytics.sectionComparison} nameKey="name" />
                            <div className="mt-2">
                                <SectionComparison data={analytics.sectionComparison} />
                            </div>
                        </Section>

                        <Section icon="fal fa-book-open" title="Subject Analytics Dashboard">
                            <SubjectComparison subjects={analytics.subjects} />
                            <Row className="mt-3 g-3">
                                <Col md={6}>
                                    <div className="text-muted mb-1" style={{ fontSize: 12.5, fontWeight: 600 }}>Average Marks by Subject</div>
                                    <SubjectMarksBarChart subjects={analytics.subjects} />
                                </Col>
                                <Col md={6}>
                                    <div className="text-muted mb-1" style={{ fontSize: 12.5, fontWeight: 600 }}>Accuracy by Subject</div>
                                    <SubjectAccuracyRadar subjects={analytics.subjects} />
                                </Col>
                                <Col md={6}>
                                    <div className="text-muted mb-1" style={{ fontSize: 12.5, fontWeight: 600 }}>Right vs Wrong</div>
                                    <SubjectRightWrongChart subjects={analytics.subjects} />
                                </Col>
                                <Col md={6}>
                                    <div className="text-muted mb-1" style={{ fontSize: 12.5, fontWeight: 600 }}>Rank Distribution</div>
                                    <RankDistributionChart data={analytics.rankDistribution} />
                                </Col>
                            </Row>
                        </Section>
                    </>
                )}
            </div>
        </div>
    );
}

export default ExamAnalyticsDashboard;
